import {
    NlpLlmParseErrorResponse as NlpLlmParseErrorResponseSchema,
    NlpLlmParseResponse as NlpLlmParseResponseSchema,
    NlpLlmSimilarResponse as NlpLlmSimilarResponseSchema,
    NlpLlmSimilarResult as NlpLlmSimilarResultSchema,
} from '@thinkalpha/platform-ws-client/contracts/nlp.js';
import type {
    NlpLlmParseErrorResponse,
    NlpLlmParseResponse,
    NlpLlmSimilarResponse,
} from '@thinkalpha/platform-ws-client/contracts/nlp.js';
import { z } from 'zod';

export type { NlpLlmParseErrorResponse, NlpLlmSimilarResponse };
// Re-export LLM schemas from the shared client package under local names
// so existing consumers don't need to change their imports.
export { NlpLlmParseErrorResponseSchema, NlpLlmSimilarResponseSchema };

// The LLM parse response is the canonical schema for validating successful
// /nlp/parse responses. Re-export it as `NlpParseResponseDataSchema` to keep
// backward compatibility with all existing consumers.
export const NlpParseResponseDataSchema = NlpLlmParseResponseSchema;
export type NlpParseResponseData = NlpLlmParseResponse;

// ----- Local NLP query types (no client equivalent) -----

export enum NlpQueryType {
    operand = 'operand',
    condition = 'condition',
    date = 'date',
}

export enum NlpQueryParseType {
    query = 'query',
    date = 'date',
}

// ----- Legacy NLP schemas (used by the old /v1/* endpoints) -----

export const NlpSuggestionSchema = NlpLlmSimilarResultSchema.extend({
    type: z.enum(['operands', 'conditions', 'timeframes']).optional(),
});

export const NlpSimilarRequestDataSchema = z.object({
    key: z.string().optional(),
    input: z.string(),
    isConditional: z.boolean(),
});

export const NlpSimilarResponseDataSchema = z.object({
    input: z.string(),
    similar: z.array(NlpSuggestionSchema),
});

export const NlpParseRequestDataSchema = z.object({
    type: z.nativeEnum(NlpQueryParseType),
    key: z.string().optional(),
    input: z.string(),
    isConditional: z.boolean(),
    clientId: z.string().optional(),
    isDone: z.boolean().optional(),
});

// Legacy 200 shape from /v1/parse. It parses to the canonical NlpParseResponseData
// shape, but the legacy service omits `similar` on success, so it defaults to empty.
export const NlpLegacyParseResponseDataSchema = NlpLlmParseResponseSchema.extend({
    similar: z.array(NlpSuggestionSchema).default([]),
});

// Legacy 422 error shape from /v1/parse — no client equivalent.
export const NlpParseResponseErrorSchema = z.object({
    detail: z.object({
        msg: z.string().optional(),
        similar: z.array(NlpSuggestionSchema).optional(),
    }),
});

export const NlpAutocompleteRequestDataSchema = z.object({
    input: z.string(),
    key: z.string(),
    isConditional: z.boolean(),
});

export const NlpAutocompleteResponseDataSchema = z.object({
    autocomplete: z.array(z.string()),
});

export const NlpSuggestionWithMockIdSchema = NlpSuggestionSchema.extend({
    id: z.string(),
});

export const NlpExampleSchema = NlpSuggestionSchema.extend({
    type: z.enum(['operands', 'conditions', 'timeframes']).optional(),
});

export const NlpExampleResponseSchema = z.object({
    data: z.array(NlpExampleSchema),
    count: z.number(),
    total: z.number(),
});

export const StockSymbolSchema = z.object({
    name: z.string(),
    ticker: z.string(),
});

export const CompanySuggestionCategorySchema = z.object({
    category_description: z.string(),
    category_label: z.string(),
    category_name: z.string(),
    comm_ticker_count: z.number(),
    comm_tickers_list: z.array(StockSymbolSchema),
    pref_ticker_count: z.number(),
    pref_tickers_list: z.array(StockSymbolSchema),
});

export const CompanySuggestionsResponseDataSchema = z.object({
    result: z.array(CompanySuggestionCategorySchema),
});

// ----- Inferred types -----

export type NlpSimilarRequestData = z.infer<typeof NlpSimilarRequestDataSchema>;
export type NlpSimilarResponseData = z.infer<typeof NlpSimilarResponseDataSchema>;
export type NlpParseRequestData = z.infer<typeof NlpParseRequestDataSchema>;
export type NlpParseResponseError = z.infer<typeof NlpParseResponseErrorSchema>;
export type NlpColumn = z.infer<typeof NlpLlmParseResponseSchema>['namedColumns'][number];
export type NlpAutocompleteRequestData = z.infer<typeof NlpAutocompleteRequestDataSchema>;
export type NlpAutocompleteResponseData = z.infer<typeof NlpAutocompleteResponseDataSchema>;
export type NlpSuggestion = z.infer<typeof NlpSuggestionSchema>;
export type NlpSuggestionWithMockId = z.infer<typeof NlpSuggestionWithMockIdSchema>;
export type NlpExample = z.infer<typeof NlpExampleSchema>;
export type NlpExampleResponse = z.infer<typeof NlpExampleResponseSchema>;
export type StockSymbol = z.infer<typeof StockSymbolSchema>;
export type CompanySuggestionCategory = z.infer<typeof CompanySuggestionCategorySchema>;
export type CompanySuggestionsResponseData = z.infer<typeof CompanySuggestionsResponseDataSchema>;
